import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkFrontmatter from 'remark-frontmatter';
import remarkRehype from 'remark-rehype';
import rehypeKatex from 'rehype-katex';
import rehypeShiki from '@shikijs/rehype';
import rehypeSlug from 'rehype-slug';
import rehypeStringify from 'rehype-stringify';
import yaml from 'js-yaml';

export type Frontmatter = Record<string, unknown>;

function rehypeWrapBlocks() {
  return (tree: any) => {
    if (tree.type !== 'root') return;
    tree.children = tree.children.map((node: any) => {
      if (node.type !== 'element') return node;
      const line = node.position?.start?.line;
      if (!line) return node;
      return {
        type: 'element',
        tagName: 'div',
        properties: { className: ['md-block'], dataSourceLine: line },
        children: [node],
        position: node.position,
      };
    });
  };
}

const processor = unified()
  .use(remarkParse)
  .use(remarkGfm)
  .use(remarkMath)
  .use(remarkFrontmatter, ['yaml'])
  .use(remarkRehype, { allowDangerousHtml: true })
  .use(rehypeWrapBlocks)
  .use(rehypeSlug)
  .use(rehypeKatex)
  .use(rehypeShiki, {
    themes: { light: 'github-light', dark: 'github-dark' },
    defaultColor: 'dark',
    langs: [
      'js', 'ts', 'jsx', 'tsx', 'json', 'html', 'css', 'scss',
      'bash', 'shell', 'python', 'go', 'rust', 'java', 'sql',
      'yaml', 'toml', 'md', 'diff',
    ],
  })
  .use(rehypeStringify, { allowDangerousHtml: true });

export function parseFrontmatter(md: string): { frontmatter: Frontmatter; body: string } {
  const match = md.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?/);
  if (!match) return { frontmatter: {}, body: md };
  try {
    const fm = yaml.load(match[1]) as Frontmatter;
    return { frontmatter: fm || {}, body: md };
  } catch {
    return { frontmatter: {}, body: md };
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => (
    c === '&' ? '&amp;' :
    c === '<' ? '&lt;' :
    c === '>' ? '&gt;' :
    c === '"' ? '&quot;' :
    '&#39;'
  ));
}

function frontmatterToTable(fm: Frontmatter): string {
  const rows = Object.entries(fm).map(([k, v]) => {
    const val = typeof v === 'string' ? v : (v == null ? '' : JSON.stringify(v));
    return `<tr><th>${escapeHtml(k)}</th><td>${escapeHtml(val)}</td></tr>`;
  }).join('');
  return `<div class="md-block frontmatter-block" data-source-line="1"><table class="frontmatter-table"><tbody>${rows}</tbody></table></div>`;
}

export async function render(md: string): Promise<string> {
  const { frontmatter } = parseFrontmatter(md);
  const prefix = Object.keys(frontmatter).length > 0 ? frontmatterToTable(frontmatter) : '';
  const result = await processor.process(md);
  return prefix + String(result);
}

export function wordCount(md: string): number {
  return md
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`[^`]*`/g, '')
    .replace(/[#*_>\-\[\]()]/g, ' ')
    .split(/\s+/)
    .filter(Boolean).length;
}
